import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import { changePassword } from "./api";
import { endSession } from "./session";

const MIN_PASSWORD_LENGTH = 12;
const MAX_PASSWORD_LENGTH = 128;
type Field = "oldPassword" | "newPassword" | "confirmPassword";
type Errors = Record<Field, string>;
const emptyErrors: Errors = { oldPassword: "", newPassword: "", confirmPassword: "" };

const validateField = (field: Field, value: string, values: Record<Field, string>) => {
  if (field === "oldPassword") return value ? "" : "Current password is required.";
  if (field === "newPassword") {
    if (!value) return "New password is required.";
    if (value.length < MIN_PASSWORD_LENGTH || value.length > MAX_PASSWORD_LENGTH) return `New password must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters.`;
    if (values.oldPassword && value === values.oldPassword) return "New password must be different from the current password.";
    return "";
  }
  if (!value) return "Please confirm the new password.";
  return value === values.newPassword ? "" : "New password and confirmation do not match.";
};

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [values, setValues] = useState<Record<Field, string>>({ oldPassword: "", newPassword: "", confirmPassword: "" });
  const [errors, setErrors] = useState<Errors>(emptyErrors);
  const [visible, setVisible] = useState<Record<Field, boolean>>({ oldPassword: false, newPassword: false, confirmPassword: false });
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const updateField = (field: Field, value: string) => {
    const next = { ...values, [field]: value };
    setValues(next);
    setErrorMessage("");
    setSuccessMessage("");
    setErrors((previous) => ({
      ...previous,
      [field]: validateField(field, value, next),
      ...(field === "oldPassword" || field === "newPassword" ? {
        newPassword: validateField("newPassword", next.newPassword, next),
        confirmPassword: validateField("confirmPassword", next.confirmPassword, next),
      } : {}),
    }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setErrorMessage("");
    setSuccessMessage("");
    const nextErrors: Errors = {
      oldPassword: validateField("oldPassword", values.oldPassword, values),
      newPassword: validateField("newPassword", values.newPassword, values),
      confirmPassword: validateField("confirmPassword", values.confirmPassword, values),
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setBusy(true);
    try {
      await changePassword(values.oldPassword, values.newPassword);
      setValues({ oldPassword: "", newPassword: "", confirmPassword: "" });
      setErrors(emptyErrors);
      setSuccessMessage("Password changed successfully. Please sign in again.");
      endSession(localStorage.getItem("token"));
      window.setTimeout(() => navigate("/admin", { replace: true }), 900);
    } catch (error: unknown) {
      setErrorMessage(error instanceof Error ? error.message : "Unable to change password. Please try again later.");
    } finally {
      setBusy(false);
    }
  };

  const renderField = (field: Field, label: string, autoComplete: string) => {
    const errorId = `change-password-${field}-error`;
    const hasError = Boolean(errors[field]);
    return (
      <div className="sf-password-field" key={field}>
        <label className="sf-password-label" htmlFor={`change-password-${field}`}>{label}</label>
        <div className="sf-password-control">
          <input
            id={`change-password-${field}`}
            className={`sf-password-input${hasError ? " is-invalid" : ""}`}
            type={visible[field] ? "text" : "password"}
            value={values[field]}
            autoComplete={autoComplete}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
            onChange={(event) => updateField(field, event.target.value)}
          />
          <button type="button" className="sf-password-toggle" aria-label={`${visible[field] ? "Hide" : "Show"} ${label.toLowerCase()}`} aria-pressed={visible[field]} onClick={() => setVisible((previous) => ({ ...previous, [field]: !previous[field] }))}>{visible[field] ? "Hide" : "Show"}</button>
        </div>
        {hasError && <div id={errorId} className="sf-password-error">{errors[field]}</div>}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <section className="sf-password-page" aria-labelledby="change-password-title">
        <div className="sf-password-card">
          <div className="sf-password-heading">
            <p className="sf-password-eyebrow">Account security</p>
            <h1 id="change-password-title">Change Password</h1>
            <p>Use a strong password with 12–128 characters. You will need to sign in again after it is changed.</p>
          </div>
          {errorMessage && <div className="sf-password-alert is-error" role="alert">{errorMessage}</div>}
          {successMessage && <div className="sf-password-alert is-success" role="status">{successMessage}</div>}
          <form onSubmit={onSubmit} noValidate>
            {renderField("oldPassword", "Current password", "current-password")}
            {renderField("newPassword", "New password", "new-password")}
            {renderField("confirmPassword", "Confirm new password", "new-password")}
            <button type="submit" className="sf-password-submit" disabled={busy}>{busy ? "Updating…" : "Update Password"}</button>
          </form>
        </div>
      </section>
    </DashboardLayout>
  );
};

export default ChangePassword;
